import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { USER, XLSX } from "../../config/constants";
import socket, { APP_UPDATE_AVAILABLE } from "../../socket";
import { getFieldErrors, IFieldError, interpretDataIntoXlsxFormat } from "../helpers";
import { ICustomer } from "./customersReducer";
import { IDriver } from "./driversReducer";
import { tryConfirmLogin } from "./authReducer";
import xlsx from "json-as-xlsx";
import { IStaff } from "./staffsReducer";
import { IServiceResource } from "./servicesResourcesReducer";

class ExportProductsErrs {
    form: string | null = null;
}

export interface IAvailableProduct {
    id: number;
    _id: string;
    name: string;
    categoryId: string;
    imgSrc: string;
    unit?: string;
    unitPrice: number;
}

export interface IProduct {
    _id: string;
    name: string;
    imgSrc?: string;
    categoryId: string;
    unit?: string;
    unitPrice: number;

    createdAt: string;

    deletedAt?: number;
}

export interface IProductForDelivery {
    _id: string;
    name: string;
    imgSrc: string;
    pricePerHour: number;
    pricePerDay: number;
    plannedDays: number;
    count: number;
    customer: ICustomer;
    driver: IDriver;

}

export interface IProductForPickUp {
    _id: string;
    name: string;
    imgSrc: string;
    pricePerHour: number;
    pricePerDay: number;
    daysRemaining: number;
    count: number;
    customer: ICustomer;
    driver: IDriver | null;

}

export interface IPackage2Deliver {
    items: IProductForDelivery[];
}

export interface IPackage2PickUp {
    item: IProductForPickUp[];
}

class RegProductErrs {
    index?: any;

    form: string | null = null;
    description: string | null = null;
    virtualLink: string | null = null;
    staffId?: string;
    name: string | null = null;
    productImg: string | null = null;
    pricePerDay: string | null = null;
    count: string | null = null;
    activationDate: string | null = null;
    deactivationDate: string | null = null;
}

export interface IProductsState {
    registeringProduct: boolean;
    regProductErrs: RegProductErrs;
    fetchingProducts: boolean;
    fetchProductsErr: string | null;
    products: IProduct[];
    togglingLike: boolean;
    toggleLikeErr: string | null;
    favouriteProducts: IProduct[];
    mainProduct: IProduct | null;
    availableItems: IAvailableProduct[];
    updatingProduct: boolean;
    updateProductErrs: RegProductErrs;
    exportingProducts: boolean | null;
    exportedProducts: boolean | null;
    exportProductsErrs: ExportProductsErrs;
    importingProducts: boolean;
    triedImportingProducts: boolean | null;
    importedProducts: boolean | null;
    importProductsErrs: RegProductErrs[];
    deletingProduct: boolean;
    deletedProduct: boolean;
    deleteProductErr: string | null;
}

const initialState = {
    registeringProduct: false,
    regProductErrs: new RegProductErrs(),
    fetchingProducts: false,
    fetchProductsErr: null,
    products: [],
    mainProduct: null,
    availableItems: [],
    updatingProduct: false,
    updateProductErrs: new RegProductErrs(),
    togglingLike: false,
    toggleLikeErr: null,
    favouriteProducts: [],
    exportingProducts: false,
    exportedProducts: false,
    exportProductsErrs: new ExportProductsErrs(),
    triedImportingProducts: false,
    importingProducts: false,
    importedProducts: false,
    importProductsErrs: [],
    deletingProduct: false,
    deletedProduct: false,
    deleteProductErr: null
} as IProductsState

const productsSlice = createSlice({
    name: 'products',
    initialState,
    reducers: {
        clearRegProductFieldErr: (state: IProductsState, action: PayloadAction<string>) => {
            state.regProductErrs[action.payload as keyof RegProductErrs] = null;
        },
        clearRegProductErrs: (state: IProductsState) => {
            state.regProductErrs = new RegProductErrs();
        },
        tryResetRegProductFormErr: (state: IProductsState) => {
            let shouldReset = true;

            for (let key in state.regProductErrs) {

                if (key === 'form') {
                    continue;
                }

                if (state.regProductErrs[key as keyof RegProductErrs]) {
                    shouldReset = false;
                    break;
                }
            }

            if (shouldReset) {
                state.regProductErrs.form = null;
            }
        },
        regProductStart: (state: IProductsState) => {
            state.registeringProduct = true;
        },
        regProductPass: (state: IProductsState) => {
            state.registeringProduct = false;
        },
        regProductFail: (state: IProductsState, action: PayloadAction<IFieldError[]>) => {
            state.registeringProduct = false;

            const errs = action.payload;

            for (const err of errs) {
                for (const key in state.regProductErrs) {
                    if (err.field === key) {
                        state.regProductErrs[key as keyof RegProductErrs] = err.msg;
                        break;
                    }
                }
            }


            if (!state.regProductErrs.form) {
                state.regProductErrs.form = 'Form incorrectly filled.';
            }
        },
        clearFetchProducts: (state: IProductsState) => {
            state.regProductErrs = new RegProductErrs();
            state.fetchProductsErr = null;
        },

        clearFetchProductErrs: (state: IProductsState) => {
            state.regProductErrs = new RegProductErrs();
            state.fetchProductsErr = null;

        },

        fetchProductsStart: (state: IProductsState) => {
            state.fetchingProducts = true;
        },
        fetchProductsPass: (state: IProductsState, action: PayloadAction<IProduct[]>) => {
            state.fetchingProducts = false;

            state.products = action.payload;
        },
        fetchProductsFail: (state: IProductsState, action: PayloadAction<string>) => {
            state.fetchingProducts = false;
            state.fetchProductsErr = action.payload;
        },
        setMainProduct: (state: IProductsState, action: PayloadAction<IProduct | null>) => {
            state.mainProduct = action.payload;
        },
        clearUpdateProductFieldErr: (state: IProductsState, action: PayloadAction<string>) => {
            state.updateProductErrs[action.payload as keyof RegProductErrs] = null;
        },
        clearUpdateProductErrs: (state: IProductsState) => {
            state.updateProductErrs = new RegProductErrs();
        },
        tryResetUpdateProductFormErr: (state: IProductsState) => {
            let shouldReset = true;

            for (let key in state.updateProductErrs) {

                if (key === 'form') {
                    continue;
                }

                if (state.updateProductErrs[key as keyof RegProductErrs]) {
                    shouldReset = false;
                    break;
                }
            }

            if (shouldReset) {
                state.updateProductErrs.form = null;
            }
        },
        updateProductStart: (state: IProductsState) => {
            state.updatingProduct = true;
        },
        updateProductPass: (state: IProductsState, action: PayloadAction<IProduct>) => {
            state.updatingProduct = false;
            // state.mainCustomer = action.payload;
        },
        updateProductFail: (state: IProductsState, action: PayloadAction<IFieldError[]>) => {
            state.updatingProduct = false;

            const errs = action.payload;

            for (const err of errs) {
                for (const key in state.updateProductErrs) {
                    if (err.field === key) {
                        state.updateProductErrs[key as keyof RegProductErrs] = err.msg;
                        break;
                    }
                }
            }


            if (!state.updateProductErrs.form) {
                state.updateProductErrs.form = 'Form incorrectly filled.';
            }
        },
        clearToggleLikeErr:(state: IProductsState) => {
            state.toggleLikeErr = null;
        },
        toggleLikeStart: (state: IProductsState) => {
            state.toggleLikeErr = null;
            state.togglingLike = true;
        },
        toggleLikePass: (state: IProductsState, action: PayloadAction<IProduct[]>) => {
            state.favouriteProducts = action.payload;
            state.togglingLike = false;
        },
        toggleLikeFail: (state: IProductsState, action: PayloadAction<string>) => {
            state.toggleLikeErr = action.payload;
            state.togglingLike = false;
        },
        setFavouriteProducts: (state: IProductsState, action: PayloadAction<IProduct[]>) => {
            state.favouriteProducts = action.payload;
        },
        clearExportProductsFieldErr: (state: IProductsState, action: PayloadAction<string>) => {
            state.exportProductsErrs[action.payload as keyof ExportProductsErrs] = null;
        },
        clearExportProductsErrs: (state: IProductsState) => {
            state.exportProductsErrs = new ExportProductsErrs();
        },
        tryResetExportProductsFormErr: (state: IProductsState) => {
            let shouldReset = true;

            for (let key in state.exportProductsErrs) {

                if (key === 'form') {
                    continue;
                }

                if (state.exportProductsErrs[key as keyof ExportProductsErrs]) {
                    shouldReset = false;
                    break;
                }
            }

            if (shouldReset) {
                state.exportProductsErrs.form = null;
            }
        },
        exportProductsStart: (state: IProductsState) => {
            state.exportingProducts = true;
            state.exportedProducts = false;
        },
        exportProductsPass: (state: IProductsState) => {
            state.exportingProducts = false;
            state.exportedProducts = true;
        },
        exportProductsFail: (state: IProductsState, action: PayloadAction<IFieldError[]>) => {
            state.exportingProducts = false;


            const errs = action.payload;

            for (const err of errs) {
                for (const key in state.exportProductsErrs) {
                    if (err.field === key) {
                        state.exportProductsErrs[key as keyof ExportProductsErrs] = err.msg;
                        break;
                    }
                }
            }


            if (!state.exportProductsErrs.form) {
                state.exportProductsErrs.form = 'Form incorrectly filled.';
            }
        },
        clearImportProductsFieldErr: (state: IProductsState, action: PayloadAction<string>) => {
            state.importProductsErrs = [];
        },
        clearImportProductsErrByIndex: (state: IProductsState, action: PayloadAction<number>) => {

            const index = action.payload;

            let newErrs = [];

            if (index < state.importProductsErrs.length) {
                for (let i = 0; i < index; i++) {
                    newErrs[i] = state.importProductsErrs[i];
                }

                for (let i = index; i < (state.importProductsErrs.length - 1); i++) {
                    newErrs[i] = (state.importProductsErrs)[i + 1];
                }

                state.importProductsErrs = newErrs;
            }
        },
        clearResetTriedImportingProducts: (state: IProductsState) => {
            state.triedImportingProducts = false;
        },
        clearImportProductsErrs: (state: IProductsState) => {
            state.importProductsErrs = [];
        },
        tryResetImportProductsFormErr: (state: IProductsState) => {

        },
        importProductsStart: (state: IProductsState) => {

            state.importProductsErrs = [];

            state.importingProducts = true;
            state.triedImportingProducts = false;
            state.importedProducts = false;

        },
        importProductsDone: (state: IProductsState) => {
            state.importingProducts = false;
            state.triedImportingProducts = true;
        },
        importProductsPass: (state: IProductsState) => {
            state.importingProducts = false;
            state.triedImportingProducts = true;
            state.importedProducts = true;
        },
        addErr2ImportProductsErrs: (state: IProductsState, action: PayloadAction<RegProductErrs>) => {

            let errs = state.importProductsErrs;

            errs = [...errs, action.payload];

            state.importProductsErrs = errs;
        },
        importProductsFail: (state: IProductsState) => {
            state.importingProducts = false;
        },
        clearDeletProductErr: (state: IProductsState) => {
            state.deleteProductErr = null;
        },
        resetDeletedProduct: (state: IProductsState) => {
            state.deletedProduct = false;
        },
        deleteProductStart: (state: IProductsState) => {
            state.deleteProductErr = null;
            state.deletedProduct = false;
            state.deletingProduct = true;
        },
        deleteProductPass: (state: IProductsState) => {
            state.deletedProduct = true;
            state.deletingProduct = false;
        },
        deleteProductFail: (state: IProductsState, action: PayloadAction<string>) => {
            state.deleteProductErr = action.payload;
            state.deletingProduct = false;
        }
    }
});

export const {
    clearRegProductFieldErr,
    clearRegProductErrs,
    tryResetRegProductFormErr,
    regProductStart,
    regProductPass,
    regProductFail,
    clearFetchProducts,
    fetchProductsStart,
    fetchProductsPass,
    fetchProductsFail,
    setMainProduct,
    clearFetchProductErrs,
    clearUpdateProductFieldErr,
    clearUpdateProductErrs,
    tryResetUpdateProductFormErr,
    updateProductStart,
    updateProductPass,
    updateProductFail,
    setFavouriteProducts,
    clearToggleLikeErr,
    toggleLikeStart,
    toggleLikePass,
    toggleLikeFail,
    clearImportProductsErrByIndex,
    clearExportProductsErrs,
    clearExportProductsFieldErr,
    tryResetExportProductsFormErr,
    exportProductsStart,
    exportProductsPass,
    exportProductsFail,
    clearResetTriedImportingProducts,
    clearImportProductsErrs,
    clearImportProductsFieldErr,
    tryResetImportProductsFormErr,
    importProductsStart,
    importProductsDone,
    importProductsPass,
    addErr2ImportProductsErrs,
    importProductsFail,
    clearDeletProductErr,
    resetDeletedProduct,
    deleteProductStart,
    deleteProductPass,
    deleteProductFail
} = productsSlice.actions;


interface ITryRegProductArg {
    data: FormData;
    authToken: string;
    setRegistered: (truth: boolean) => void;
    setServiceResourceId?: (_id: string | null) => void;
}

export const tryRegProduct = createAsyncThunk('products/tryRegProduct', (arg: ITryRegProductArg, { dispatch }) => {
    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/product`;

    const { data, authToken, setRegistered, setServiceResourceId } = arg;

    dispatch(regProductStart());

    axios.post(endpoint, data, {
        headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${authToken}`
        }
    }).then(res => {
        socket.emit(APP_UPDATE_AVAILABLE, USER);

        if (res?.data?.serviceResourceId && setServiceResourceId) {
            setServiceResourceId(res?.data?.serviceResourceId);
        }

        dispatch(regProductPass());
        setRegistered(true);
    }).catch(err => {

        const data = err?.response?.data;

        let fieldErrors: IFieldError[] = [];

        if (data?.errors) {
            fieldErrors = getFieldErrors(data.errors);
        } else if (data?.err) {
            fieldErrors = [{ field: 'form', msg: data.err }];
        } else {
            fieldErrors = [{ field: 'form', msg: 'Check your network connection.' }];
        }

        dispatch(regProductFail(fieldErrors));
    })
});

interface ITryFetchProductArg {
    authToken: string;
    type?: string;
}

export const tryFetchProducts = createAsyncThunk('products/tryFetchProducts', (arg: ITryFetchProductArg, { dispatch }) => {

    const { authToken, type } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/products${type ? `?type=${type}` : ''}`;

    dispatch(fetchProductsStart());
    axios.get(endpoint, {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
        }
    }).then(res => {
        dispatch(fetchProductsPass(res.data));
    }).catch(err => {
        const data = err?.response?.data;

        if (data?.err && (data.err === "Session expired")) {
            dispatch(tryConfirmLogin({ authToken }));
            return;
        }

        if (data?.err) {
            dispatch(fetchProductsFail(data.err));
        } else {
            dispatch(fetchProductsFail('Check your network connection.'))
        }

    });
});


interface ITryUpdateProductArg {
    productId: string;
    data: FormData;
    authToken: string;
    setUpdated: (truth: boolean) => void
}

export const tryUpdateProduct = createAsyncThunk('products/tryUpdateProduct', (arg: ITryUpdateProductArg, { dispatch }) => {

    const { productId, data, authToken, setUpdated } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/products/${productId}`;

    dispatch(updateProductStart());

    axios.put(endpoint, data, {
        headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${authToken}`
        }
    }).then(res => {
        socket.emit(APP_UPDATE_AVAILABLE, USER);
        dispatch(updateProductPass(res.data));
        setUpdated(true);
    }).catch(err => {

        const data = err?.response?.data;

        let fieldErrors: IFieldError[] = [];

        if (data?.errors) {
            fieldErrors = getFieldErrors(data.errors);
        } else if (data?.err) {
            fieldErrors = [{ field: 'form', msg: data.err }];
        } else {
            fieldErrors = [{ field: 'form', msg: 'Check your network connection.' }];
        }

        dispatch(updateProductFail(fieldErrors));
    })
});

interface ITryToggleProductLikeArg {
    authToken: string;
    productId: string;
}

export const tryToggleRentalLike = createAsyncThunk('products/tryToggleLike', (arg: ITryToggleProductLikeArg, {dispatch}) => {
    const {authToken, productId} = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/products/${productId}/toggle-like`;

    dispatch(toggleLikeStart())
    axios.post(endpoint, {}, {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
        }
    }).then(res => {
        dispatch(toggleLikePass(res.data?.favouriteProducts ?? []));
    })
    .catch(err => {
        const data = err?.response?.data;
        let error = '';

        if (data?.errMsg) {
            error = data.errMsg;
        } else if (data?.errors) {
            error = getFieldErrors(data.errors)[0].msg;
        } else {
            error = 'Check your network connection.';
        }

        dispatch(toggleLikeFail(error));
    });

});

interface ITryExportProductsArg {
    format: string;
    authToken: string;
}

export const tryExportProducts = createAsyncThunk("servicesPlans/tryExportProducts", (arg: ITryExportProductsArg, { dispatch }) => {

    const { authToken, format } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/products-export`;

    dispatch(exportProductsStart());
    axios.get(endpoint, {
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${authToken}`
        }
    }).then(res => {
        const data = res.data;

        if (data.length === 0) {
            const fieldErrors = [{ field: 'form', msg: 'Export Empty.' }];

            dispatch(exportProductsFail(fieldErrors));
        } else {

            switch (format) {
                case XLSX:
                default:
                    const fileSettings = {
                        fileName: "Products"        //TODO: dynamically generate the name, based on the parameters used to fetch the data
                    };

                    try {
                        xlsx(interpretDataIntoXlsxFormat(data), fileSettings);
                    } catch (err) {
                        alert("err");
                        console.log(err);
                    }
                    break;
            }

            dispatch(exportProductsPass());
        }
    }).catch(err => {
        const data = err?.response?.data;

        if (data?.err && (data.err === "Session expired")) {
            dispatch(tryConfirmLogin({ authToken }));
            return;
        }

        let fieldErrors: IFieldError[] = [];

        if (data?.errors) {
            fieldErrors = getFieldErrors(data.errors);
        } else if (data?.err) {
            fieldErrors = [{ field: 'form', msg: data.err }];
        } else {
            console.log(data);
            fieldErrors = [{ field: 'form', msg: 'Check your network connection.' }];
        }

        dispatch(exportProductsFail(fieldErrors));
    })


});

interface IDeleteProductArg {
    authToken: string;
    productId: string;
}

export const tryDeleteProduct = createAsyncThunk('products/deleteProduct', (arg: IDeleteProductArg, { dispatch }) => {
    const { authToken, productId } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/products/${productId}`;

    dispatch(deleteProductStart())
    axios.delete(endpoint, {
        headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
        }
    }).then(res => {
        dispatch(deleteProductPass());
        socket.emit(APP_UPDATE_AVAILABLE, USER);
    }).catch(err => {
        const data = err?.response?.data;

        if (data?.err && (data.err === "Session expired")) {
            dispatch(tryConfirmLogin({ authToken }));
            return;
        }
        let error = '';

        if (data?.err) {
            error = data.err;
        } else if (data?.errors) {
            error = getFieldErrors(data.errors)[0].msg;
        } else {
            error = 'Check your network connection.';
        }

        dispatch(deleteProductFail(error));
    });
});


export default productsSlice.reducer;
